'use client';

import { useEffect, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { colors, typography } from '@/styles/theme';

export interface ColumnOption {
  key: string;
  label: string;
  alwaysVisible?: boolean; // true 表示不允许用户关闭
}

interface ColumnConfigProps {
  storageKey: string;
  options: ColumnOption[];
  value: string[]; // 当前可见的 key 集合
  onChange: (next: string[]) => void;
  triggerLabel?: string;
}

/** 从 localStorage 读取用户偏好（若不存在则返回所有 key） */
export function loadColumnPrefs(storageKey: string, defaults: string[]): string[] {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) return parsed;
  } catch {}
  return defaults;
}

export function saveColumnPrefs(storageKey: string, value: string[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {}
}

export function ColumnConfig({ storageKey, options, value, onChange, triggerLabel }: ColumnConfigProps) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState<string[]>(value);

  useEffect(() => {
    if (open) setLocal(value);
  }, [open, value]);

  const toggle = (key: string) => {
    const next = local.includes(key) ? local.filter((k) => k !== key) : [...local, key];
    setLocal(next);
    onChange(next);
    saveColumnPrefs(storageKey, next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          <Settings2 className="w-3.5 h-3.5" />
          {triggerLabel ?? 'Columns'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 p-0">
        <div className="px-3 py-2 text-xs font-semibold" style={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
          {triggerLabel ?? 'Columns'}
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {options.map((opt) => {
            const checked = local.includes(opt.key);
            return (
              <label
                key={opt.key}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50"
              >
                <Checkbox
                  checked={checked}
                  disabled={opt.alwaysVisible}
                  onCheckedChange={() => !opt.alwaysVisible && toggle(opt.key)}
                />
                <span className={typography.body}>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
